import { searchKnowledge } from 'actions/workflows-v2/workflowsV2';
import type { WidgetArticleSearchOrigin } from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import React from 'react';
import { useEffect, useRef } from 'react';
import { debounce } from 'utils/debounce';

export const useWidgetArticleSearch = ({
  origin,
  query,
}: {
  origin: WidgetArticleSearchOrigin;
  query: string;
}) => {
  const dispatch = useAppDispatch();
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);

  /**
   * Used to prevent the search from being called on the first render
   * with a populated query.
   *
   * Note: Does not work in React.StrictMode.
   */
  const isInitialMount = useRef(true);

  const search = React.useMemo(
    () =>
      debounce((query: string, origin: WidgetArticleSearchOrigin) => {
        dispatch(searchKnowledge({ origin, query }));
      }, 500),
    [dispatch],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // only the primary tab should trigger the search
    if (!isCurrentTabPrimary) {
      return;
    }

    if (query && query.length > 3) {
      search(query, origin);
    }
  }, [isCurrentTabPrimary, origin, query, search]);
};
